import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import type { CreateQuoteFieldErrors, FormDataState } from '../../hooks/useCreateQuote';

interface CargoDetailsSectionProps {
  formData: FormDataState;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  fieldErrors: CreateQuoteFieldErrors['cargo'];
  inputClass: string;
  labelClass: string;
}

const MAX_VOLUME_CM3 = 200_000_000;
const MAX_WEIGHT_KG = 120_000;

const parsePositiveNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
};

export const CargoDetailsSection: React.FC<CargoDetailsSectionProps> = ({
  formData,
  handleChange,
  handleFileChange,
  fieldErrors,
  inputClass,
  labelClass
}) => {
  const { t } = useTranslation();
  const sectionTitleClass = "text-lg leading-none tracking-tight mb-4 text-foreground";
  const sectionId = 'cargo-details-section-title';
  const RequiredMark = () => <span className="text-red-500 ml-1">*</span>;

  const showOversizedWarning = useMemo(() => {
    const length = parsePositiveNumber(String(formData.cargo_length ?? ''));
    const width = parsePositiveNumber(String(formData.cargo_width ?? ''));
    const height = parsePositiveNumber(String(formData.cargo_height ?? ''));
    const weight = parsePositiveNumber(String(formData.cargo_weight ?? ''));

    const volumeExceeds = length !== null && width !== null && height !== null
      && length * width * height > MAX_VOLUME_CM3;
    const weightExceeds = weight !== null && weight > MAX_WEIGHT_KG;

    return volumeExceeds || weightExceeds;
  }, [formData.cargo_length, formData.cargo_width, formData.cargo_height, formData.cargo_weight]);

  const renderFieldErrors = (errors: string[]) => (
    errors.length > 0 ? (
      <ul className="mt-2 space-y-1 text-sm text-destructive">
        {errors.map((error, index) => (
          <li key={`${error}-${index}`}>{t(error)}</li>
        ))}
      </ul>
    ) : null
  );

  return (
    <div role="group" aria-labelledby={sectionId}>
      <h4 id={sectionId} className={sectionTitleClass}>{t('quote.form.sections.cargo')}</h4>

      <div className="mb-4">
        <label className={labelClass}>{t('quote.form.fields.cargo.title')}<RequiredMark /></label>
        <input
          type="text"
          name="cargo_title"
          className={inputClass}
          value={formData.cargo_title}
          onChange={handleChange}
          required
          placeholder={t('quote.form.placeholders.cargo_title')}
        />
        {renderFieldErrors(fieldErrors.cargo_title)}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <label className={labelClass}>{t('quote.form.fields.dimensions.length')}<RequiredMark /></label>
          <input type="number" name="cargo_length" className={inputClass} value={formData.cargo_length} onChange={handleChange} required />
          {renderFieldErrors(fieldErrors.cargo_length)}
        </div>
        <div>
          <label className={labelClass}>{t('quote.form.fields.dimensions.width')}<RequiredMark /></label>
          <input type="number" name="cargo_width" className={inputClass} value={formData.cargo_width} onChange={handleChange} required />
          {renderFieldErrors(fieldErrors.cargo_width)}
        </div>
        <div>
          <label className={labelClass}>{t('quote.form.fields.dimensions.height')}<RequiredMark /></label>
          <input type="number" name="cargo_height" className={inputClass} value={formData.cargo_height} onChange={handleChange} required />
          {renderFieldErrors(fieldErrors.cargo_height)}
        </div>
        <div>
          <label className={labelClass}>{t('quote.form.fields.dimensions.weight')}<RequiredMark /></label>
          <input type="number" name="cargo_weight" className={inputClass} value={formData.cargo_weight} onChange={handleChange} required />
          {renderFieldErrors(fieldErrors.cargo_weight)}
        </div>
      </div>

      {showOversizedWarning && (
        <div
          className="mt-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
          role="status"
          aria-live="polite"
          aria-atomic="true"
          data-testid="oversized-cargo-warning"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" aria-hidden="true" />
          <span>{t('quote.form.warnings.oversized_cargo')}</span>
        </div>
      )}
    </div>
  );
};
